import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Checkbox,
  Col,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Radio,
  Row,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckOutlined,
  DollarOutlined,
  PrinterOutlined,
  SaveOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkflowStrip, WorkspaceLayout } from '@/components/enterprise';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import { PrintableLeaveSettlement } from '@/components/hr/PrintableLeaveSettlement';
import { ENTITY_TYPE_HR_LEAVE_SETTLEMENT } from '@/constants/approval-entity-types';
import { useChartAccountsByType } from '@/hooks/useSalesInvoice';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { useEmployeeOptions } from './shared/useEmployeeOptions';
import { buildLinearWorkflow, hrmsStatusColor, showApprovalPanel } from './shared/hrmsStatus';
import { getOrFindFromList } from './shared/hrmsFetch';
import { openHrmsPrintOverlay } from './shared/openHrmsPrintOverlay';

const { Text } = Typography;

type SettlementMode = 'Payroll' | 'SalaryAdvance';

const EDITABLE = new Set(['Draft', 'Pending', 'PendingApproval', 'Rejected']);
const BOOKABLE = new Set(['Draft', 'Pending', 'PendingApproval', 'Approved', 'Rejected']);

const LeaveSettlementDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { empLabel } = useEmployeeOptions({ employment_status: null });
  const [loading, setLoading] = useState<string | null>(null);
  const [form] = Form.useForm();
  const modeWatch = Form.useWatch('settlement_mode', form) as SettlementMode | undefined;
  const eligibleWatch = Form.useWatch('eligible_days', form);

  const { data: expenseAccounts = [] } = useChartAccountsByType('Expense', false);
  const { data: liabilityAccounts = [] } = useChartAccountsByType('Liability', false);
  const accountOpts = (rows: { id: string; account_code?: string; account_name?: string }[]) =>
    rows.map((a) => ({
      value: a.id,
      label: `${a.account_code ?? ''} — ${a.account_name ?? a.id}`.trim(),
    }));

  const { data: row, isLoading } = useQuery({
    queryKey: hrmsKeys.leaveSettlement(id || ''),
    queryFn: () =>
      getOrFindFromList(
        () => hrmsService.getLeaveSettlement(id!),
        () => hrmsService.listLeaveSettlements(),
        id!
      ),
    enabled: Boolean(id),
  });

  const { data: glDefaults } = useQuery({
    queryKey: [...hrmsKeys.all, 'leave-settlement-gl-defaults'],
    queryFn: () => hrmsService.getLeaveSettlementGlDefaults(),
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!row) return;
    form.setFieldsValue({
      eligible_days: Number(row.eligible_days) || 0,
      other_amount: Number(row.other_amount) || 0,
      notes: row.notes || '',
      settlement_mode: row.settlement_mode || undefined,
      target_period_year: row.target_period_year || dayjs().year(),
      target_period_month: row.target_period_month || dayjs().month() + 1,
      advance_installments: row.advance_installments || 1,
      create_recovery_loan: false,
      expense_account_id: row.expense_account_id || glDefaults?.expense_account_id || undefined,
      payable_account_id: row.payable_account_id || glDefaults?.payable_account_id || undefined,
    });
  }, [row, form, glDefaults]);

  const workflow = useMemo(
    () =>
      buildLinearWorkflow(
        ['Draft', 'Pending', 'Approved', 'Booked', 'Paid'],
        row?.status === 'PendingApproval' ? 'Pending' : row?.status || 'Draft',
        { rejectedStatuses: ['Rejected'] }
      ),
    [row?.status]
  );

  const leaveAmountPreview = useMemo(() => {
    const rate = Number(row?.daily_rate || 0);
    const days = Number(eligibleWatch ?? row?.eligible_days ?? 0);
    return Math.round(rate * days * 100) / 100;
  }, [row?.daily_rate, row?.eligible_days, eligibleWatch]);

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: hrmsKeys.leaveSettlement(id!) });
    await queryClient.invalidateQueries({ queryKey: hrmsKeys.leaveSettlements() });
  };

  const run = async (key: string, fn: () => Promise<unknown>, okMsg: string) => {
    setLoading(key);
    try {
      await fn();
      message.success(okMsg);
      await invalidate();
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || `${okMsg} failed`);
    } finally {
      setLoading(null);
    }
  };

  if (isLoading) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Leave settlement" />}>
        <Spin />
      </WorkspaceLayout>
    );
  }

  if (!row) {
    return (
      <WorkspaceLayout
        header={
          <EnterprisePageHeader
            documentLabel="Leave settlement"
            primaryActions={
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/hr/leave-settlements')}>
                Back
              </Button>
            }
          />
        }
      >
        <Card>Leave settlement not found.</Card>
      </WorkspaceLayout>
    );
  }

  const status = String(row.status || '');
  const canEdit = EDITABLE.has(status);
  const canSubmit = status === 'Draft' || status === 'Rejected';
  const canApprove = status === 'Pending' || status === 'PendingApproval' || status === 'Draft';
  const canBook =
    BOOKABLE.has(status) && !row.monthly_adjustment_id && !row.accrual_journal_voucher_id;
  const canMarkPaid =
    status === 'Booked' && row.settlement_mode === 'SalaryAdvance' && !row.accrual_journal_voucher_id;

  const saveDraft = async () => {
    const v = await form.validateFields([
      'eligible_days',
      'other_amount',
      'notes',
      'settlement_mode',
      'target_period_year',
      'target_period_month',
      'advance_installments',
    ]);
    await run(
      'save',
      () =>
        hrmsService.updateLeaveSettlement(id!, {
          eligible_days: v.eligible_days,
          other_amount: v.other_amount,
          notes: v.notes,
          settlement_mode: v.settlement_mode || null,
          target_period_year: v.target_period_year,
          target_period_month: v.target_period_month,
          advance_installments: v.advance_installments,
        }),
      'Saved'
    );
  };

  const book = async () => {
    const fields = [
      'settlement_mode',
      'target_period_year',
      'target_period_month',
      'advance_installments',
      'create_recovery_loan',
    ];
    if (form.getFieldValue('settlement_mode') === 'SalaryAdvance') {
      fields.push('expense_account_id', 'payable_account_id');
    }
    const v = await form.validateFields(fields);
    if (!v.settlement_mode) {
      message.warning('Select Payroll or Pay now before booking');
      return;
    }
    if (canEdit) {
      await hrmsService.updateLeaveSettlement(id!, {
        eligible_days: form.getFieldValue('eligible_days'),
        other_amount: form.getFieldValue('other_amount'),
        notes: form.getFieldValue('notes'),
        settlement_mode: v.settlement_mode,
        target_period_year: v.target_period_year,
        target_period_month: v.target_period_month,
        advance_installments: v.advance_installments,
      });
    }
    await run(
      'book',
      () =>
        hrmsService.bookLeaveSettlement(id!, {
          settlement_mode: v.settlement_mode,
          target_period_year: v.target_period_year,
          target_period_month: v.target_period_month,
          advance_installments: v.advance_installments,
          create_recovery_loan:
            v.settlement_mode === 'SalaryAdvance' ? !!v.create_recovery_loan : undefined,
          expense_account_id:
            v.settlement_mode === 'SalaryAdvance' ? v.expense_account_id : undefined,
          payable_account_id:
            v.settlement_mode === 'SalaryAdvance' ? v.payable_account_id : undefined,
        }),
      v.settlement_mode === 'SalaryAdvance'
        ? 'Accrual invoice created — Finance must post the JV, then pay via Payment Voucher'
        : 'Settlement booked'
    );
  };

  const printDoc = () => {
    const calc = (row.calc_json || {}) as Record<string, unknown>;
    openHrmsPrintOverlay((onClose) => (
      <PrintableLeaveSettlement
        onClose={onClose}
        data={{
          settlement_number: row.settlement_number,
          employee_name: empLabel(row.employee_id),
          leave_application_number: String(calc.leave_application_number || row.leave_application_id || ''),
          leave_type: row.leave_type,
          leave_year: row.leave_year,
          from_date: calc.from_date ? dayjs(String(calc.from_date)).format('DD-MMM-YYYY') : undefined,
          to_date: calc.to_date ? dayjs(String(calc.to_date)).format('DD-MMM-YYYY') : undefined,
          balance_available: row.balance_available,
          balance_opening: row.balance_opening,
          balance_accrued: row.balance_accrued,
          balance_carried: row.balance_carried,
          balance_consumed: row.balance_consumed,
          balance_pending: row.balance_pending,
          balance_encashed: row.balance_encashed,
          leave_days: row.leave_days,
          eligible_days: row.eligible_days,
          daily_rate: row.daily_rate,
          leave_amount: row.leave_amount,
          other_amount: row.other_amount,
          net_amount: row.net_amount,
          settlement_mode: row.settlement_mode,
          target_period_year: row.target_period_year,
          target_period_month: row.target_period_month,
          status: row.status,
          notes: row.notes,
        }}
      />
    ));
  };

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Leave settlement"
          documentNumber={row.settlement_number  || '—'}
          statusTag={<Tag color={hrmsStatusColor(status)}>{status}</Tag>}
          subtitle={<Text type="secondary">{empLabel(row.employee_id)}</Text>}
          primaryActions={
            <Space wrap>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/hr/leave-settlements')}>
                Back
              </Button>
              <Button icon={<PrinterOutlined />} onClick={printDoc}>
                Print
              </Button>
              {canEdit && (
                <Button icon={<SaveOutlined />} loading={loading === 'save'} onClick={saveDraft}>
                  Save draft
                </Button>
              )}
              {canSubmit && (
                <Button
                  icon={<SendOutlined />}
                  loading={loading === 'submit'}
                  onClick={() =>
                    run('submit', () => hrmsService.submitLeaveSettlement(id!), 'Submitted')
                  }
                >
                  Submit
                </Button>
              )}
              {canApprove && (
                <Button
                  icon={<CheckOutlined />}
                  loading={loading === 'approve'}
                  onClick={() =>
                    run('approve', () => hrmsService.approveLeaveSettlement(id!), 'Approved')
                  }
                >
                  Approve
                </Button>
              )}
              {canBook && (
                <Button type="primary" icon={<DollarOutlined />} loading={loading === 'book'} onClick={book}>
                  Book
                </Button>
              )}
              {canMarkPaid && (
                <Button
                  type="primary"
                  icon={<CheckOutlined />}
                  loading={loading === 'paid'}
                  onClick={() =>
                    run('paid', () => hrmsService.markLeaveSettlementPaid(id!), 'Marked paid')
                  }
                >
                  Mark paid
                </Button>
              )}
            </Space>
          }
        />
      }
      workflow={<WorkflowStrip stages={workflow.stages} current={workflow.current} compact />}
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="UAE / MENA leave settlement"
        description={
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            <li>
              Shows leave balance snapshot, eligible paid days, daily rate (leave-encashment salary heads ÷ 30), and
              leave amount. Use <strong>Other amount</strong> for ticket / allowance add-ons.
            </li>
            <li>
              <strong>Payroll</strong> — leave pay booked as an Addition on the selected payroll period (settled with
              salary).
            </li>
            <li>
              <strong>Pay now</strong> — creates a draft accrual JV (Dr expense / Cr employee payable; accounts selects
              the credit account). Finance posts the JV, then pays via Payment Voucher → Employee Payment. Optional:
              create a recovery loan EMI if the advance must be recovered from salary.
            </li>
            <li>Applies to paid leave only (e.g. Annual). One settlement per leave application.</li>
          </ul>
        }
      />

      <Row gutter={16}>
        <Col xs={24} lg={16}>
          <Card title="Balance snapshot" style={{ marginBottom: 16 }}>
            <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small">
              <Descriptions.Item label="Available">{row.balance_available ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Opening">{row.balance_opening ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Accrued">{row.balance_accrued ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Carried">{row.balance_carried ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Consumed">{row.balance_consumed ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Pending">{row.balance_pending ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Encashed">{row.balance_encashed ?? '—'}</Descriptions.Item>
            </Descriptions>
          </Card>

          <Card title="Settlement amounts" style={{ marginBottom: 16 }}>
            <Descriptions column={{ xs: 1, sm: 2 }} size="small">
              <Descriptions.Item label="Employee">{empLabel(row.employee_id)}</Descriptions.Item>
              <Descriptions.Item label="Leave type">{row.leave_type}</Descriptions.Item>
              <Descriptions.Item label="Leave year">{row.leave_year ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Leave application">
                {row.leave_application_id ? (
                  <a onClick={() => navigate(`/hr/leaves/${row.leave_application_id}`)}>
                    {String(
                      (row.calc_json as any)?.leave_application_number || 'Open leave application'
                    )}
                  </a>
                ) : (
                  '—'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Leave days">{row.leave_days ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Daily rate">{row.daily_rate ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Leave amount">{row.leave_amount ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Net amount">
                <Text strong>{row.net_amount ?? '—'}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Booked at">
                {row.booked_at ? dayjs(row.booked_at).format('DD-MMM-YYYY HH:mm') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Paid at">
                {row.paid_at ? dayjs(row.paid_at).format('DD-MMM-YYYY HH:mm') : '—'}
              </Descriptions.Item>
            </Descriptions>

            <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
              {canEdit ? (
                <>
                  <Row gutter={16}>
                    <Col xs={24} sm={8}>
                      <Form.Item name="eligible_days" label="Eligible days" rules={[{ required: true }]}>
                        <InputNumber min={0.01} step={0.5} style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={8}>
                      <Form.Item name="other_amount" label="Other amount">
                        <InputNumber style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={8}>
                      <Form.Item label="Leave amount (calc)">
                        <InputNumber disabled style={{ width: '100%' }} value={leaveAmountPreview} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item name="notes" label="Notes">
                    <Input.TextArea rows={2} />
                  </Form.Item>
                </>
              ) : (
                <Descriptions column={{ xs: 1, sm: 2 }} size="small">
                  <Descriptions.Item label="Eligible days">{row.eligible_days ?? '—'}</Descriptions.Item>
                  <Descriptions.Item label="Other amount">{row.other_amount ?? '—'}</Descriptions.Item>
                  <Descriptions.Item label="Notes" span={2}>
                    {row.notes || '—'}
                  </Descriptions.Item>
                </Descriptions>
              )}

              <Card type="inner" title="HR decision" style={{ marginTop: 8 }}>
                <Form.Item
                  name="settlement_mode"
                  label="Settlement mode"
                  rules={[{ required: canBook, message: 'Select a mode' }]}
                >
                  <Radio.Group disabled={!canEdit && !canBook}>
                    <Radio value="Payroll">Payroll (with salary)</Radio>
                    <Radio value="SalaryAdvance">Pay now (accrue &amp; pay via Finance)</Radio>
                  </Radio.Group>
                </Form.Item>
                <Row gutter={16}>
                  <Col xs={12} sm={8}>
                    <Form.Item name="target_period_year" label="Period year" rules={[{ required: true }]}>
                      <InputNumber
                        min={2020}
                        max={2100}
                        style={{ width: '100%' }}
                        disabled={!canEdit && !canBook}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={8}>
                    <Form.Item name="target_period_month" label="Period month" rules={[{ required: true }]}>
                      <InputNumber
                        min={1}
                        max={12}
                        style={{ width: '100%' }}
                        disabled={!canEdit && !canBook}
                      />
                    </Form.Item>
                  </Col>
                  {(modeWatch === 'SalaryAdvance' || row.settlement_mode === 'SalaryAdvance') && (
                    <Col xs={24} sm={8}>
                      <Form.Item name="advance_installments" label="Recovery installments">
                        <InputNumber
                          min={1}
                          max={36}
                          style={{ width: '100%' }}
                          disabled={!canEdit && !canBook}
                        />
                      </Form.Item>
                    </Col>
                  )}
                </Row>
                {(modeWatch === 'SalaryAdvance' || row.settlement_mode === 'SalaryAdvance') && (
                  <>
                    <Row gutter={16}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="expense_account_id"
                          label="Expense account (debit)"
                          rules={[
                            {
                              required: canBook && modeWatch === 'SalaryAdvance',
                              message: 'Select expense account',
                            },
                          ]}
                          extra="Leave / salary expense — debited on the accrual invoice"
                        >
                          <Select
                            showSearch
                            optionFilterProp="label"
                            options={accountOpts(expenseAccounts as any)}
                            disabled={!canEdit && !canBook}
                            placeholder="Select expense account"
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="payable_account_id"
                          label="Employee payable (credit)"
                          rules={[
                            {
                              required: canBook && modeWatch === 'SalaryAdvance',
                              message: 'Select payable account',
                            },
                          ]}
                          extra="Accounts selects the credit liability — cleared when Payment Voucher posts"
                        >
                          <Select
                            showSearch
                            optionFilterProp="label"
                            options={accountOpts(liabilityAccounts as any)}
                            disabled={!canEdit && !canBook}
                            placeholder="Select employee payable account"
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                    {canBook && modeWatch === 'SalaryAdvance' && (
                      <Form.Item name="create_recovery_loan" valuePropName="checked">
                        <Checkbox>
                          Also create salary recovery loan (EMI) — only if advance must be recovered from payroll
                        </Checkbox>
                      </Form.Item>
                    )}
                  </>
                )}
                {(row.monthly_adjustment_id ||
                  row.loan_id ||
                  row.accrual_journal_voucher_id ||
                  row.payment_voucher_id) && (
                  <Descriptions size="small" column={1}>
                    {row.monthly_adjustment_id && (
                      <Descriptions.Item label="Payroll adjustment">
                        {row.monthly_adjustment_id}
                      </Descriptions.Item>
                    )}
                    {row.loan_id && (
                      <Descriptions.Item label="Advance loan">
                        <a onClick={() => navigate(`/hr/loans/${row.loan_id}`)}>Open loan</a>
                      </Descriptions.Item>
                    )}
                    {row.accrual_journal_voucher_id && (
                      <Descriptions.Item label="Accrual JV">
                        <a onClick={() => navigate(`/fin/gl/journals`)}>
                          {String(
                            (row.calc_json as any)?.accrual_journal_voucher_number ||
                              'Open journals'
                          )}
                        </a>
                        <Text type="secondary"> — Finance must post, then pay via Payment Voucher</Text>
                      </Descriptions.Item>
                    )}
                    {row.payment_voucher_id && (
                      <Descriptions.Item label="Payment voucher">
                        <a onClick={() => navigate(`/fin/payment-vouchers/${row.payment_voucher_id}`)}>
                          Open payment voucher
                        </a>
                      </Descriptions.Item>
                    )}
                  </Descriptions>
                )}
              </Card>
            </Form>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          {showApprovalPanel(row) && (
            <ApprovalPanel
              entityType={ENTITY_TYPE_HR_LEAVE_SETTLEMENT}
              entityId={row.id}
              extraInvalidateQueryKeys={[
                hrmsKeys.leaveSettlement(id!),
                hrmsKeys.leaveSettlements(),
              ]}
            />
          )}
        </Col>
      </Row>
    </WorkspaceLayout>
  );
};

export default LeaveSettlementDetailPage;
